import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import copyIcon from './assets/copy-icon.svg';
import shareIcon from './assets/share-icon.svg';
import classes from './GameUiDialog.module.scss';
import { shareService } from './share-service';

export type ShareDialogProp = {
  imageUrl: string;
  text: string;
  title?: string;
  showCopyLink?: boolean;
  autoClose?: boolean;
  onClose?: () => void;
  onTrackEvent?: (event: string) => void;
};

export function ShareDialog({
  imageUrl,
  text,
  title,
  showCopyLink,
  autoClose = true,
  onClose,
  onTrackEvent,
}: ShareDialogProp) {
  const { t } = useTranslation();

  useEffect(() => {
    onTrackEvent?.('impression');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onShare = async () => {
    try {
      onTrackEvent?.('share');
      const res = await shareService.shareUrl(imageUrl, text, title);
      if (res?.success && autoClose) {
        onClose?.();
      }
    } catch (error) {
      console.error('Error sharing', error);
    }
  };

  const onCopyLink = async () => {
    try {
      onTrackEvent?.('copy_url');
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(imageUrl);
      } else {
        window.open(imageUrl, '_blank');
      }
    } catch {
      window.open(imageUrl, '_blank');
    }
  };

  const onImageError = () => {
    if (autoClose) {
      onClose?.();
      return;
    }
    console.error('Share image failed to load', imageUrl.replace(/[\r\n]/g, '_'));
  };

  return (
    <div className={classes.cover}>
      <div className={classes.shareDialog} data-testid="share-image-dialog">
        <div
          className={classes.closeBtn}
          data-testid="share-dialog-close-button"
          onClick={() => {
            onTrackEvent?.('close');
            onClose?.();
          }}
        >
          &times;
        </div>
        <img alt="Share" className={classes.bg} src={imageUrl} onError={onImageError} />
        <div className={classes.shareButtons}>
          <button className={classes.shareButton} data-testid="share-image-button" onClick={onShare}>
            <img alt="" className={classes.shareIconImg} src={shareIcon} />
            <span>{t('dialogs.shareDialog.share')}</span>
          </button>
          {showCopyLink && (
            <button className={classes.copyImageButton} data-testid="copy-image-button" onClick={onCopyLink}>
              <img alt="" className={classes.copyIcon} height="20" src={copyIcon} width="20" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
